import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import type {
  ILoginRequest,
  IMembershipApplicationImage,
  IMembershipApplicationRequest,
  IOrganizationAddressRequest,
  IOrganizationAddressResponse,
  IOrganizationRequest,
  IOrganizationResponse,
  ITokenLoginRequest,
  IUserResponse,
} from './interfaces/auth.interface';
import { lastValueFrom } from 'rxjs';
import type {
  IArtist,
  ICalculateFeeResponse,
  ICategory,
  IProductMedia,
  IProductSupport,
  IProductCertificate,
} from './interfaces/core.interface';
import type { ICreditCard } from './interfaces/mulpay.interface';
import { BaseApiService } from './base-api.service';
import type { IPaginatedRequest, IPaginatedResponse } from './interfaces/pagination.interface';
import type {
  IManagedProductResponse,
  IProductImage,
  IProductRequest,
  IProductResponse,
  IReturnProductRequest,
  IReturnProductResponse,
} from './interfaces/product.interface';
import type { ITransactionDetailResponse, ITransactionRequest, ITransactionResponse } from './interfaces/transaction.interface';
import type { IShippingResponse } from './interfaces/shipping.interface';
import type {
  IBankAccountRequest,
  IBankAccountResponse,
  IBankBranchesResponse,
  IBanksResponse,
  IBankTransfer,
} from './interfaces/bank-account.interface';
import type { IPurchaseRequest, IPurchaseResponse } from './interfaces/purchase.interface';
import type { INotificationResponse } from './interfaces/notification.interface';
import type { ICouponResponse } from './interfaces/coupon.interface';
import type { IPublicProductRequest, IPublicProductRequestPost } from './interfaces/product-request.interface';
import { IMailReceivingSetting, IMailReceivingSettingRequest } from './interfaces/mail-receiving-setting.interface';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  readonly #http = inject(HttpClient);
  readonly #baseApi = inject(BaseApiService);

  requestMembershipApplication(params: IMembershipApplicationRequest) {
    return lastValueFrom(this.#baseApi.post('/membership-application', params));
  }

  requestMembershipApplicationImage(params: File) {
    const formData = new FormData();
    formData.append('image_original', params);
    return lastValueFrom(this.#baseApi.post<IMembershipApplicationImage>('/membership-application-image', formData));
  }

  async requestLogin(body: ILoginRequest) {
    await lastValueFrom(this.#baseApi.post('/sign-in', body));
  }

  login(body: ITokenLoginRequest) {
    return lastValueFrom(this.#baseApi.post<IUserResponse>('/sign-in/token', body));
  }

  async logout() {
    await lastValueFrom(this.#baseApi.post('/sign-out', {}));
  }

  getCurrentUser() {
    return lastValueFrom(this.#baseApi.get<IUserResponse>('/current-user'));
  }

  getOrganizationAddress() {
    return lastValueFrom(this.#baseApi.get<IOrganizationAddressResponse>('/organization_address'));
  }

  putOrganization(organizationId: string, params: IOrganizationRequest) {
    return lastValueFrom(this.#baseApi.put<IOrganizationResponse>(`/organizations/${organizationId}`, params));
  }

  createOrganizationAddress(params: IOrganizationAddressRequest) {
    return lastValueFrom(this.#baseApi.post<IOrganizationAddressResponse>('/organization_address', params));
  }

  putOrganizationAddress(params: IOrganizationAddressRequest) {
    return lastValueFrom(this.#baseApi.put<IOrganizationAddressResponse>('/organization_address', params));
  }

  getManegedProducts(params?: Record<string, string | number | null>) {
    return lastValueFrom(this.#baseApi.get<IPaginatedResponse<IManagedProductResponse>>('/managed-products', params));
  }

  getProducts(params?: Record<string, string | number | boolean | null>) {
    return lastValueFrom(this.#baseApi.get<IPaginatedResponse<IProductResponse>>('/products', params));
  }

  getProductDetail(id: string) {
    return lastValueFrom(this.#baseApi.get<IProductResponse>(`/products/${id}`));
  }

  createMyProduct(params: Partial<IProductRequest>) {
    return lastValueFrom(this.#baseApi.post('/my-products', params));
  }

  updateMyProduct(productId: string, params: Partial<IProductRequest>) {
    return lastValueFrom(this.#baseApi.put(`/my-products/${productId}`, params));
  }

  getMyProductDetail(id: string) {
    return lastValueFrom(this.#baseApi.get<IProductResponse>(`/my-products/${id}`));
  }

  deleteMyProduct(id: string) {
    return lastValueFrom(this.#baseApi.delete<IProductResponse>(`/my-products/${id}`));
  }

  createPurchaseRequest(params: IPurchaseRequest) {
    return lastValueFrom(this.#baseApi.post<IPurchaseRequest>('/purchase-requests', params));
  }

  getPurchaseRequests(productId: string, params: IPaginatedRequest) {
    return lastValueFrom(this.#baseApi.get<IPaginatedResponse<IPurchaseResponse>>(`/products/${productId}/purchase-requests`, params));
  }

  createTransaction(params: ITransactionRequest) {
    return lastValueFrom(this.#baseApi.post<ITransactionResponse>('/transactions', params));
  }

  getTransactionDetail(id: string) {
    return lastValueFrom(this.#baseApi.get<ITransactionDetailResponse>(`/transactions/${id}`));
  }

  createReturnProduct(params: IReturnProductRequest) {
    return lastValueFrom(this.#baseApi.post('/return-products', params));
  }

  getReturnProduct(id: string) {
    return lastValueFrom(this.#baseApi.get<IReturnProductResponse>(`/transactions/${id}/return-products`));
  }

  getShipping(id: string) {
    return lastValueFrom(this.#baseApi.get<IShippingResponse>(`/transactions/${id}/shipping`));
  }

  createShippingAddressRecipient(transactionId: string, params: IOrganizationAddressRequest) {
    return lastValueFrom(
      this.#baseApi.post<IOrganizationAddressResponse>(`/transactions/${transactionId}/shipping-address/recipient`, params),
    );
  }

  createShippingAddressSender(transactionId: string, params: IOrganizationAddressRequest) {
    return lastValueFrom(
      this.#baseApi.post<IOrganizationAddressResponse>(`/transactions/${transactionId}/shipping-address/sender`, params),
    );
  }

  createBankAccount(params: IBankAccountRequest) {
    return lastValueFrom(this.#baseApi.post<IBankAccountResponse>(`/bank-accounts`, params));
  }

  createMyProductRequest(params: Partial<IPublicProductRequestPost>) {
    return lastValueFrom(this.#baseApi.post('/my-product-requests', params));
  }

  updateMyProductRequest(productRequestId: string, params: Partial<IPublicProductRequestPost>) {
    return lastValueFrom(this.#baseApi.put(`/my-product-requests/${productRequestId}`, params));
  }

  getProductRequest(params?: Record<string, string | number | boolean | null>) {
    return lastValueFrom(this.#baseApi.get<IPaginatedResponse<IPublicProductRequest>>('/public-product-requests', params));
  }

  getProductRequestDetail(id: string) {
    return lastValueFrom(this.#baseApi.get<IPublicProductRequest>(`/public-product-requests/${id}`));
  }

  getBankAccount() {
    return lastValueFrom(this.#baseApi.get<IBankAccountResponse[]>('/bank-accounts'));
  }

  getMailReceivingSetting() {
    return lastValueFrom(this.#baseApi.get<IMailReceivingSetting>('/mail-receiving-settings'));
  }

  updateMailReceivingSetting(params: IMailReceivingSettingRequest) {
    return lastValueFrom(this.#baseApi.put('/mail-receiving-settings', params));
  }

  getBanks() {
    return lastValueFrom(this.#http.get<IBanksResponse[]>('https://bank.teraren.com/banks.json?per=10000', {}));
  }
  getBankBranches(bankCode: string) {
    return lastValueFrom(this.#http.get<IBankBranchesResponse[]>(`https://bank.teraren.com/banks/${bankCode}/branches.json?per=10000`, {}));
  }

  putCompleteTransaction(transactionId: string) {
    return lastValueFrom(this.#baseApi.put(`/transactions/${transactionId}/status/finished`, {}));
  }

  putCompleteReturnProduct(transactionId: string) {
    return lastValueFrom(this.#baseApi.put(`/transactions/${transactionId}/return-products/status/finished`, {}));
  }

  getCategories(params?: IPaginatedRequest) {
    return lastValueFrom(this.#baseApi.get<IPaginatedResponse<ICategory>>('/categories', params));
  }

  requestProductImage(params: File) {
    const formData = new FormData();
    formData.append('image_original', params);
    return this.#baseApi.uploadImageWithProgress<IProductImage>('/product-images', formData);
  }

  requestProductRequestImage(params: File) {
    const formData = new FormData();
    formData.append('image_original', params);
    return this.#baseApi.uploadImageWithProgress<IProductImage>('/product-request-images', formData);
  }

  getNotifications(params?: IPaginatedRequest) {
    return lastValueFrom(this.#baseApi.get<IPaginatedResponse<INotificationResponse>>('/notifications', params));
  }

  getArtists(params?: IPaginatedRequest) {
    return lastValueFrom(this.#baseApi.get<IPaginatedResponse<IArtist>>('/artists', params));
  }

  getProductMedium(params?: IPaginatedRequest) {
    return lastValueFrom(this.#baseApi.get<IPaginatedResponse<IProductMedia>>('/product-medium', params));
  }

  getProductSupports(params?: IPaginatedRequest) {
    return lastValueFrom(this.#baseApi.get<IPaginatedResponse<IProductSupport>>('/product-supports', params));
  }

  getProductCertificates(params?: IPaginatedRequest) {
    return lastValueFrom(this.#baseApi.get<IPaginatedResponse<IProductCertificate>>('/product-certificates', params));
  }

  getSettlementCreditCard() {
    return lastValueFrom(this.#baseApi.get<ICreditCard>('/settlement/credit-card'));
  }

  createSettlementCreditCard(mp_token: string) {
    const params = {
      mp_token,
    };
    return lastValueFrom(this.#baseApi.post<ICreditCard>('/settlement/credit-card', params));
  }

  createCreditCard(params: { transaction_id: string }) {
    return lastValueFrom(this.#baseApi.post('/payment/gmopg-credit-card', params));
  }

  createBankTransfer(params: { transaction_id: string }) {
    return lastValueFrom(this.#baseApi.post<IBankTransfer>('/payment/gmopg-bank-transfer', params));
  }

  listBankTransfer(params: { transaction_id: string }) {
    return lastValueFrom(this.#baseApi.get<IPaginatedResponse<IBankTransfer>>('/payment/gmopg-bank-transfer', params));
  }

  getCoupons() {
    return lastValueFrom(this.#baseApi.get<IPaginatedResponse<ICouponResponse>>('/coupons'));
  }

  getCalculateFee(price: number) {
    return lastValueFrom(this.#baseApi.post<ICalculateFeeResponse>('/fee', { price: price }));
  }
}

export const CustomAuthErrorHeader = {
  authError: 'dk-auth-error',
};
